import { LanguageSelect } from '@glade-ai/ui';
import throttle from 'lodash/throttle';
import { m } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type FC, useContext, useEffect, useRef } from 'react';

import EditButton from '@/components/EditButton';
import NewTextEllipsisDetails from '@/components/NewTextEllipsisDetails';
import UserImage from '@/components/UserImage';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { useIsMobile, useUser } from '@/hooks';
import TranslationContext from '@/providers/TranslationProvider/TranslationContext';
import { SIZES } from '@/styles/media';
import { type getCreator } from '@/tsClient';

import type { ThisCreator, ThisProduct } from '../CreatorBySlug';
import HeroLinks from '../HeroLinks';
import MobileNav from '../MobileNav';

import s from './Header.module.scss';

const Header: FC<{
  isWidget: boolean;
  tab: string;
  setTab: (tab: string) => void;
  creator: Awaited<ReturnType<typeof getCreator>> | null;
  creatorFromProducts: ThisCreator | null;
  canEdit: boolean;
  consultationProduct: ThisProduct | null;
  enableServicesTab: boolean;
  broadcastComments: ThisProduct['comments'];
  allHandbooks: ThisProduct[];
}> = ({
  isWidget,
  tab,
  setTab,
  creator,
  creatorFromProducts,
  canEdit,
  consultationProduct,
  enableServicesTab,
  broadcastComments,
  allHandbooks,
}) => {
  const { language, setLanguage } = useContext(TranslationContext);
  const isMobile = useIsMobile(SIZES.md);
  const router = useRouter();
  const [user] = useUser();
  const headerRef = useRef<HTMLDivElement>(null);

  const handleEditProfileClick = (): void => {
    router.push(getUrl(IDENTIFIERS.DASHBOARD_ACCOUNT_EDIT_BUSINESS_DETAILS));
  };

  // Set viewport inset CSS variables
  // TODO(will): This is a mitigation – the broader layout needs to be refactored so this component doesn’t need to
  //  compute its size.
  useEffect(() => {
    const root = document.documentElement;

    const updateHeight = throttle(() => {
      if (headerRef.current) {
        const { height } = headerRef.current.getBoundingClientRect();
        const mb = parseFloat(window.getComputedStyle(headerRef.current).marginBottom) || 0;
        root.style.setProperty('--t0', `${height + mb}px`);
      }
    }, 100);

    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
      updateHeight.cancel();
      root.style.removeProperty('--t0');
    };
  }, []);
  // TODO(will): This is also a mitigation – remove when the broader layout is fixed.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--t1', isWidget ? '34px' : '0px');
    root.style.setProperty('--b1', isWidget ? '80px' : '0px');
    return () => {
      root.style.removeProperty('--t1');
      root.style.removeProperty('--b1');
    };
  }, [isWidget]);

  if (isWidget) {
    return (
      <>
        <LanguageSelect variant='ghost' className={s.langWidget} language={language} onLanguageChange={setLanguage} />
        <div ref={headerRef} className={s.categoriesWidget}>
          {enableServicesTab && (
            <label>
              <input type='radio' name='tab' checked={tab === 'Services'} onChange={() => setTab('Services')} />
              Services
            </label>
          )}
          {consultationProduct && (
            <label>
              <input type='radio' name='tab' checked={tab === 'Bookings'} onChange={() => setTab('Bookings')} />
              Book a Consultation
            </label>
          )}
          {broadcastComments && <label>Community</label>}
          {allHandbooks.length > 0 && (
            <label>
              <input type='radio' name='tab' checked={tab === 'Bytes'} onChange={() => setTab('Bytes')} />
              Library
            </label>
          )}
          {creator && (
            <label>
              <input
                type='radio'
                name='tab'
                checked={tab === 'Profile'}
                onChange={() => {
                  if (user) {
                    router.push(getUrl(IDENTIFIERS.USER_PROFILE_WORKFLOWS, { creatorSlug: creator.slug }));
                  } else {
                    setTab('Profile');
                  }
                }}
              />
              Profile
            </label>
          )}
          <label>
            <input
              type='radio'
              name='tab'
              checked={tab === 'Chat'}
              onChange={() => {
                setTab('Chat');
              }}
            />
            Chat
          </label>
        </div>
      </>
    );
  }

  return (
    <div ref={headerRef} className={s['creator-page__header']}>
      <div className={s['creator-page__header__gradient']} />
      <div className={s['creator-page__header-wrapper']}>
        <div className={s['creator-page__header-author-text-only']}>
          <div className={s.authorHeader}>
            {creator ? (
              <>
                <Link href={getUrl(IDENTIFIERS.CREATOR, { creatorSlug: creator.slug })}>
                  {creator?.logoForBranding?.url ? (
                    <Image src={creator.logoForBranding.url} alt={creator?.name ?? 'logo'} width={240} height={80} />
                  ) : (
                    <UserImage
                      color='var(--color-primary)'
                      size={isMobile ? 60 : 80}
                      url={creator.logoCompact?.url || creator.person?.image?.url}
                      name={creator.name}
                    />
                  )}
                </Link>
              </>
            ) : (
              <div />
            )}
            <LanguageSelect variant='ghost' className={s.lang} language={language} onLanguageChange={setLanguage} />
          </div>
          <div>
            <h1 data-translate='false' className={s.creatorName}>
              {creatorFromProducts?.name || creator?.name}
            </h1>
            <div className={s['creator-page__header-details-text-only']}>
              {creatorFromProducts?.oneLiner && <NewTextEllipsisDetails text={creatorFromProducts.oneLiner} />}

              {canEdit && <EditButton onClick={handleEditProfileClick} label='Edit your profile' />}
              {creator?.slug && <HeroLinks creatorSlug={creator.slug} canEdit={canEdit} />}
            </div>
          </div>
        </div>

        {isMobile ? (
          <MobileNav
            enableServicesTab={enableServicesTab}
            consultationProduct={consultationProduct}
            creator={creator}
            showBroadcasts={!!broadcastComments?.length}
            showHandbooks={!!allHandbooks?.length}
            tab={tab}
            setTab={setTab}
          />
        ) : (
          <div className={s['categories-text-only']}>
            {(enableServicesTab || canEdit) && (
              <label>
                <input type='radio' name='tab' checked={tab === 'Services'} onChange={() => setTab('Services')} />
                Services
                {tab === 'Services' && <m.div layoutId='tab-underline' id='underline' className={s.underline} />}
              </label>
            )}

            {consultationProduct && (
              <label>
                <input type='radio' name='tab' checked={tab === 'Bookings'} onChange={() => setTab('Bookings')} />
                Book a Consultation
                {tab === 'Bookings' && <m.div layoutId='tab-underline' id='underline' className={s.underline} />}
              </label>
            )}

            {!!broadcastComments?.length && (
              <label>
                <input type='radio' name='tab' checked={tab === 'Broadcasts'} onChange={() => setTab('Broadcasts')} />
                Community
                {tab === 'Broadcasts' && <m.div layoutId='tab-underline' id='underline' className={s.underline} />}
              </label>
            )}
            {allHandbooks.length > 0 && (
              <label>
                <input type='radio' name='tab' checked={tab === 'Bytes'} onChange={() => setTab('Bytes')} />
                Library
                {tab === 'Bytes' && <m.div layoutId='tab-underline' id='underline' className={s.underline} />}
              </label>
            )}

            {!canEdit && creator && (
              <label>
                <input
                  type='radio'
                  name='tab'
                  checked={tab === 'Profile'}
                  onChange={() => {
                    if (user) {
                      router.push(getUrl(IDENTIFIERS.USER_PROFILE_WORKFLOWS, { creatorSlug: creator.slug }));
                    } else {
                      setTab('Profile');
                    }
                  }}
                />
                Profile
                {tab === 'Profile' && <m.div layoutId='tab-underline' id='underline' className={s.underline} />}
              </label>
            )}

            {!canEdit && (
              <label>
                <input
                  type='radio'
                  name='tab'
                  checked={tab === 'Chat'}
                  onChange={() => {
                    setTab('Chat');
                  }}
                />
                Chat
                {tab === 'Chat' && <m.div layoutId='tab-underline' id='underline' className={s.underline} />}
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
